import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useLocation } from 'wouter';
import { Plus, Truck, Search, Sparkles, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent } from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { FormDrawer } from '@/components/ui/form-drawer';
import { useAuth } from '@/lib/auth-context';
import { api } from '@/lib/api';

interface Vendor {
  id: string;
  name: string;
  code?: string;
  vendorNumber?: string;
  externalId?: string;
  status: string;
  email?: string;
  phone?: string;
  website?: string;
  paymentTerms?: string;
  vendorTypes?: string[];
  notes?: string;
}

interface VendorFormData {
  name: string;
  code: string;
  vendorTypes: string[];
  notes: string;
}

// Mock vendor types - in real app these would come from settings
const vendorTypeOptions = [
  { value: 'supplier', label: 'Supplier' },
  { value: 'manufacturer', label: 'Manufacturer' },
  { value: 'distributor', label: 'Distributor' },
  { value: 'service_provider', label: 'Service Provider' },
];

export function VendorsPage() {
  const { sessionId } = useAuth();
  const queryClient = useQueryClient();
  const [, setLocation] = useLocation();
  
  const [newVendorDrawerOpen, setNewVendorDrawerOpen] = useState(false);
  const [searchValue, setSearchValue] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  
  const [formData, setFormData] = useState<VendorFormData>({
    name: '',
    code: '',
    vendorTypes: [],
    notes: '',
  });

  const [formErrors, setFormErrors] = useState<Record<string, string>>({});
  const [isSuggestingCode, setIsSuggestingCode] = useState(false);

  // Fetch vendors
  const { data: vendors, isLoading } = useQuery({
    queryKey: ['vendors', sessionId],
    queryFn: async () => {
      const result = await api.get<{ vendors: Vendor[] }>('/vendors', sessionId);
      return result.vendors;
    },
    enabled: !!sessionId,
  });

  // Filter vendors
  const filteredVendors = (vendors || []).filter(vendor => {
    if (searchValue) {
      const q = searchValue.toLowerCase();
      const matches =
        vendor.name.toLowerCase().includes(q) ||
        vendor.code?.toLowerCase().includes(q) ||
        vendor.vendorNumber?.toLowerCase().includes(q) ||
        vendor.externalId?.toLowerCase().includes(q);
      if (!matches) return false;
    }
    if (statusFilter !== 'all' && vendor.status !== statusFilter) return false;
    return true;
  });

  // Suggest code with AI
  const handleSuggestCode = async () => {
    if (!formData.name || isSuggestingCode) return;
    
    setIsSuggestingCode(true);
    try {
      const response = await fetch('/api/codes/suggest', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(sessionId ? { 'X-Session-ID': sessionId } : {}),
        },
        body: JSON.stringify({
          entityType: 'vendor',
          name: formData.name,
          useAI: true,
        }),
      });
      
      if (response.ok) {
        const data = await response.json();
        setFormData({ ...formData, code: data.code });
      }
    } catch (error) {
      console.error('Failed to suggest code:', error);
    } finally {
      setIsSuggestingCode(false);
    }
  };

  // Create vendor mutation
  const createMutation = useMutation({
    mutationFn: async (data: VendorFormData) => {
      return api.post('/vendors', { ...data, status: 'active' }, sessionId);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['vendors'] });
      setNewVendorDrawerOpen(false);
      resetForm();
    },
    onError: (error: Error) => {
      const msg = error.message || 'Failed to create vendor';
      if (/code/i.test(msg)) {
        setFormErrors({ code: msg });
      } else {
        setFormErrors({ general: msg });
      }
    },
  });

  const resetForm = () => {
    setFormData({
      name: '',
      code: '',
      vendorTypes: [],
      notes: '',
    });
    setFormErrors({});
  };

  const handleDrawerClose = (open: boolean) => {
    setNewVendorDrawerOpen(open);
    if (!open) {
      resetForm();
    }
  };

  const validateForm = (): boolean => {
    const errors: Record<string, string> = {};
    
    if (!formData.name.trim()) {
      errors.name = 'Vendor name is required';
    }
    
    if (formData.code && formData.code.length > 16) {
      errors.code = 'Code must be 16 characters or less';
    }
    
    if (formData.code && !/^[A-Z0-9-]*$/.test(formData.code)) {
      errors.code = 'Use uppercase letters, digits, or hyphens';
    }
    
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (validateForm()) {
      createMutation.mutate(formData);
    }
  };

  const handleRowClick = (vendorId: string) => {
    setLocation(`/vendors/${vendorId}`);
  };

  const handleVendorTypeToggle = (typeValue: string) => {
    const current = formData.vendorTypes || [];
    if (current.includes(typeValue)) {
      setFormData({ ...formData, vendorTypes: current.filter(v => v !== typeValue) });
    } else {
      setFormData({ ...formData, vendorTypes: [...current, typeValue] });
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Vendors</h1>
          <p className="mt-1 text-sm text-gray-500">
            Manage your vendor accounts
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button onClick={() => setNewVendorDrawerOpen(true)}>
            <Plus className="h-4 w-4 mr-2" />
            New Vendor
          </Button>
        </div>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="p-4">
          <div className="flex items-center gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
              <Input
                placeholder="Search vendors, codes, or IDs..."
                value={searchValue}
                onChange={(e) => setSearchValue(e.target.value)}
                className="pl-9"
              />
            </div>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-[180px]">
                <SelectValue placeholder="All Statuses" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Statuses</SelectItem>
                <SelectItem value="active">Active</SelectItem>
                <SelectItem value="inactive">Inactive</SelectItem>
                <SelectItem value="on_hold">On Hold</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Table */}
      <Card>
        <CardContent className="p-0">
          {isLoading ? (
            <div className="text-center py-12 text-gray-500">
              Loading vendors...
            </div>
          ) : filteredVendors.length === 0 ? (
            <div className="text-center py-12">
              <Truck className="h-12 w-12 text-gray-400 mx-auto mb-4" />
              <p className="text-gray-500">No vendors found</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Vendor
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Code
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Status
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Payment Terms
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Contact
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {filteredVendors.map((vendor) => (
                    <tr
                      key={vendor.id}
                      onClick={() => handleRowClick(vendor.id)}
                      className="hover:bg-gray-50 cursor-pointer"
                    >
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className="text-sm font-medium text-gray-900">
                          {vendor.name}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {vendor.code || '-'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                          vendor.status === 'active'
                            ? 'bg-green-100 text-green-800'
                            : vendor.status === 'on_hold'
                            ? 'bg-yellow-100 text-yellow-800'
                            : 'bg-gray-100 text-gray-800'
                        }`}>
                          {vendor.status === 'on_hold' ? 'On Hold' : vendor.status}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {vendor.paymentTerms || '-'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {vendor.email || vendor.phone || '-'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Create Vendor Drawer */}
      <FormDrawer
        open={newVendorDrawerOpen}
        onOpenChange={handleDrawerClose}
        title="New Vendor"
        description="Create a new vendor"
        formId="create-vendor"
        isPending={createMutation.isPending}
        submitLabel="Create Vendor"
        size="md"
        data-testid="drawer-new-vendor"
      >
        <form id="create-vendor" onSubmit={handleSubmit} className="space-y-6">
          {/* Vendor Name */}
          <div className="space-y-2">
            <Label htmlFor="name">Vendor Name *</Label>
            <Input
              id="name"
              variant="flat"
              placeholder="Enter vendor name"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              onBlur={() => {
                if (formData.name && !formData.code && !isSuggestingCode) {
                  handleSuggestCode();
                }
              }}
              className={formErrors.name ? 'border-destructive' : ''}
              data-testid="input-vendor-name"
            />
            {formErrors.name && (
              <p className="text-sm text-destructive">{formErrors.name}</p>
            )}
          </div>

          {/* Code with AI Suggest */}
          <div className="space-y-2">
            <Label htmlFor="code">Code</Label>
            <div className="flex gap-1">
              <Input
                id="code"
                variant="flat"
                placeholder="ACM"
                maxLength={16}
                value={formData.code}
                onChange={(e) => setFormData({ ...formData, code: e.target.value.toUpperCase() })}
                className={formErrors.code ? 'border-destructive' : ''}
                data-testid="input-vendor-code"
              />
              <Button
                type="button"
                variant="outline"
                size="icon"
                title="Suggest code"
                disabled={!formData.name || isSuggestingCode}
                onClick={handleSuggestCode}
                data-testid="button-suggest-code"
              >
                {isSuggestingCode ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <Sparkles className="h-4 w-4" />
                )}
              </Button>
            </div>
            {formErrors.code && (
              <p className="text-sm text-destructive">{formErrors.code}</p>
            )}
          </div>

          {/* Vendor Type - Multi-checkbox */}
          <div className="space-y-2">
            <Label>Vendor Type</Label>
            <div className="grid grid-cols-2 gap-2 rounded-md border p-3">
              {vendorTypeOptions.length === 0 && (
                <p className="col-span-2 text-xs text-muted-foreground">
                  No vendor types defined yet. Add some in Settings.
                </p>
              )}
              {vendorTypeOptions.map((opt) => {
                const checked = (formData.vendorTypes || []).includes(opt.value);
                return (
                  <label
                    key={opt.value}
                    className="flex items-center gap-2 text-sm cursor-pointer"
                  >
                    <Checkbox
                      checked={checked}
                      onCheckedChange={() => handleVendorTypeToggle(opt.value)}
                    />
                    <span>{opt.label}</span>
                  </label>
                );
              })}
            </div>
          </div>

          {/* Notes */}
          <div className="space-y-2">
            <Label htmlFor="notes">Notes</Label>
            <Textarea
              id="notes"
              variant="flat"
              placeholder="Add any additional notes about this vendor..."
              className="min-h-[100px]"
              value={formData.notes}
              onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
              data-testid="textarea-vendor-notes"
            />
          </div>

          {/* General Error */}
          {formErrors.general && (
            <div className="p-3 rounded-md border border-destructive/50 bg-destructive/10">
              <p className="text-sm text-destructive">{formErrors.general}</p>
            </div>
          )}
        </form>
      </FormDrawer>
    </div>
  );
}
